import {
  Stack,
  Grid,
  Divider,
  Center,
  Box,
  Flex,
  Button,
  Loader,
  Group,
  Text,
} from '@mantine/core';
import { Proposal } from '../../../interfaces/types';
import Requirements from './Requirements';
import CaseInfo from './CaseInfo';
import CaseDescription from './CaseDescription';
import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { IconPlus, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGeneration } from '@/hooks/useGeneration';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProposal } from '../context/ProposalContext';
import SaveProposal from '../PostProposalGeneration/Buttons/SaveProposal';
export default function PreProposalGeneration() {
  const heightRefLeft = useRef(null);
  const heightRefRight = useRef(null);
  const [height, setHeight] = useState<number | null>(null);
  const { t, i18n } = useTranslation();

  const {
    proposal,
    dispatchProposal,
    userFeedback,
    dispatchUserFeedback,
    otherData,
    dispatchOtherData,
  } = useProposal();

  const { generating } = userFeedback;
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { proposalId } = location.state as { proposalId: string };
  const [saveStatus, setSaveStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const { generateAll, cancelField, cancelAll } = useGeneration({
    dispatchProposal,
    dispatchUserFeedback,
    dispatchOtherData,
  });

  const handleGenerate = async () => {
    const config = {
      input: proposal.input,
      sector: proposal.sector,
      documentLanguage: proposal.language,
      constraints: proposal.constraints,
      proposal,
    };

    await generateAll(config);
  };

  const handleCancel = () => {
    cancelAll();
  };

  useEffect(() => {
    if (!heightRefLeft.current) return;

    const updateHeight = () => {
      if (!heightRefLeft.current || !heightRefRight.current) return;

      const newHeight = Math.max(
        (heightRefLeft.current as HTMLElement).getBoundingClientRect().height,
        (heightRefRight.current as HTMLElement).getBoundingClientRect().height
      );
      setHeight(newHeight);
    };

    // Initial measurement
    updateHeight();

    // Set up the ResizeObserver to listen for changes in the element's size
    const observer = new ResizeObserver(() => {
      updateHeight();
    });
    if (heightRefLeft.current) {
      observer.observe(heightRefLeft.current);
    }
    if (heightRefRight.current) {
      observer.observe(heightRefRight.current);
    }
    // Optionally, listen for window resize events too
    window.addEventListener('resize', updateHeight);

    // Clean up on unmount
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return (
    <Stack
      p="xl"
      miw="1000px"
      mx="xl"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: '8px',
        backgroundColor: 'var(--mantine-color-body)',
      }}
    >
      <Grid gutter="md" h="100%" columns={11} p="xl">
        {/* Left Column */}
        <Grid.Col span={5}>
          <Stack ref={heightRefLeft}>
            {/* Top Left - CaseInfo */}
            <CaseInfo />

            {/* Bottom Left - Requirements */}
            <Requirements />
          </Stack>
        </Grid.Col>

        {/* Vertical Divider 
            Although this looks disgusting, it is literally the only way to get the divider to work (as of 11/05/2025)
             - chatgpt was clueless and this is the only solution I could could think of by myself

             Also, for some reason even though I think the height should be the same and ok to set stack height to be the same as the height of the left column, I need a factor in there
        */}
        <Grid.Col span={1} h={(height && height * 0.98) || '100%'}>
          <Stack h="100%" justify="center" align="start">
            <Box
              w="50%"
              h="100%"
              p={0}
              m={0}
              style={{ borderRight: '1px solid var(--mantine-color-gray-3)' }}
            ></Box>
          </Stack>
        </Grid.Col>

        {/* Right Column - CaseDescription */}
        <Grid.Col span={5}>
          <Box ref={heightRefRight}>
            <Stack>
              <CaseDescription height={height} />
              <Group mt="1rem" justify="flex-end">
                <SaveProposal />
                <Button
                  color={generating ? 'orange' : 'cyan'}
                  variant="light"
                  size="md"
                  leftSection={
                    generating ? <Loader size="xs" color="cyan" /> : <IconPlus size={18} />
                  }
                  onClick={() => {
                    if (generating) {
                      handleCancel();
                    } else {
                      handleGenerate();
                    }
                  }}
                >
                  {generating ? t('cancelGeneration') : t('generateProposal')}
                </Button>
              </Group>
            </Stack>
          </Box>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
